'use client';

import { useState, useEffect } from 'react';
import { redirect, useParams, useSearchParams } from 'react-router-dom';
import { CheckCircle, X } from 'lucide-react';
import { useAuth } from "../../../contexts/AuthContext";
import redirectTo from "../../../utils/redirect";
import { AuthGuard } from "../../auth/components/AuthGuard";
import { Header } from "../../landing/components/Header";
import { EventCard } from "../components/EventCard";
import { GuestList } from "../components/GuestList";
import { Guest } from "../components/GuestRow";
import { CommunicationPanel } from "../components/CommunicationPanel";
import { SideEventsManager } from "../components/SideEventsManager";
import { AttendanceManager } from "../components/AttendanceManager";
import { Event } from "../../../services/EventService";
import CoHostsManager  from '../components/CoHostsManager';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../components/ui/tabs";
import { Helmet } from 'react-helmet-async'


interface EventData {
  id: string;
  title: string;
  description: string;
  date: string;


  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
  location: string;
  organizer: {
    id: string;
    name: string;
    email: string;
    avatar?: string;
  };
  coHosts: {
    id: string;
    name: string;
    email: string;
    avatar?: string;
    status: 'accepted' | 'declined' | 'pending';
    invited_at: any
  }[];
  guests: Guest[];
  waitlist: Guest[];
  imageUrl?: string;
  limited_places: number;
  requires_approval?: boolean; // Indica si se requiere aprobación para asistir
  parent_event_id?: string; // Add this to identify if it's a side event
  type: any; // Tipo de evento
  category?: string; // Categoría del evento
  modality?: 'in-person' | 'online' | 'hybrid'; // Modalidad del evento
}

function EventPage() {
  const { isLoading, isAuthenticated, isInitialized, user } = useAuth();
  const { eventId } = useParams<{ eventId: string }>();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<'guests' | 'cohosts' | 'attendance' | 'communication' | 'sideEvents'>('guests');
  const [eventData, setEventData] = useState<EventData | null>(null);
  const [isLoadingEvent, setIsLoadingEvent] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  
  // New state for permissions
  const [userPermissions, setUserPermissions] = useState<{
    canView: boolean;
    canEdit: boolean;
    canManageAttendees: boolean;
    role: 'creator' | 'host' | 'helper' | 'none';
  } | null>(null);

  useEffect(() => {
    if (!eventId) {
      redirectTo('/dashboard');
      return;
    }

    // Verificar si el evento fue recién creado o actualizado
    const wasCreated = searchParams.get('created') === 'true';
    const wasUpdated = searchParams.get('updated') === 'true';
    if (wasCreated || wasUpdated) {
      setShowSuccessMessage(true);
    }

    fetchEventData(eventId);
  }, [eventId, searchParams]);

  // Check permissions when user and eventId are available
  useEffect(() => {
    if (user && eventId) {
      checkUserPermissions(eventId, user.id);
    }
  }, [user, eventId]);

  const checkUserPermissions = async (eventId: string, userId: string) => {
    try {
      const permissions = await Event.getUserEventPermissions(eventId, userId);
      
      if (permissions.error && !permissions.canView) {
        console.error('Permission error:', permissions.error);
        setError('You do not have permission to view this event');
        return;
      }

      setUserPermissions(permissions);
      
      if(permissions.role === 'none'){
        redirectTo('/dashboard');
        return;
      }
      // Set appropriate default tab based on role
      if (permissions.role === 'helper' || permissions.role === 'creator' || permissions.role === 'host') {
        setActiveTab('guests'); // Attendees can only see guests
      } else if (permissions.canManageAttendees) {
        setActiveTab('attendance'); // Hosts/creators see attendance by default
      } 
      
    } catch (error) {
      console.error('Error checking permissions:', error);
      setError('Error checking permissions');
    }
  };

  const fetchEventData = async (id: string) => {
    try {
      setIsLoadingEvent(true);
      setError(null);

      // Obtener el evento con sus relaciones
      const { data: eventWithRelations, error: eventError } = await Event.getById(id);

      if (eventError) {
        console.error('Error fetching event:', eventError);
        setError('Failed to load event');
        return;
      }

      if (!eventWithRelations) {
        setError('Event not found');
        return;
      }

      // Transformar los datos para que coincidan con la interfaz EventData
      const transformedEventData: EventData = {
        id: eventWithRelations.id,
        title: eventWithRelations.title,
        description: eventWithRelations.description || eventWithRelations.short_description || '',
        date: `${eventWithRelations.start_date}T${eventWithRelations.start_time || '00:00:00'}Z`,
        startDate: eventWithRelations.start_date || '00',
        endDate: eventWithRelations.end_date || '00',
        startTime: eventWithRelations.start_time || '00:00:00',
        endTime: eventWithRelations.end_time || '00:00:00',
        location: eventWithRelations.location || 'TBD',
        imageUrl: eventWithRelations.image_url || undefined,
        organizer: {
          id: '',
          name: 'Event Organizer',
          email: ''
        },
        limited_places: eventWithRelations.limited_places || 0,
        coHosts: [],
        guests: [], // TODO: Implementar cuando tengamos la tabla de invitados
        waitlist: [], // TODO: Implementar cuando tengamos la waitlist
        parent_event_id: eventWithRelations.parent_event_id ?? '',
        type: eventWithRelations.type, // Asignar tipo de evento
        category: eventWithRelations.category || undefined, // Asignar categoría del evento
        modality: eventWithRelations.modality || undefined, // Asignar modalidad del evento
      };

      // Procesar hosts si existen
      if (eventWithRelations.hosts && eventWithRelations.hosts.length > 0) {
        const mainHost = eventWithRelations.hosts.find(host => host.is_main_host);
        const coHosts = eventWithRelations.hosts.filter(host => !host.is_main_host && host.status === 'accepted');

        if (mainHost) {
          transformedEventData.organizer = {
            id: mainHost.user_id || mainHost.id,
            name: mainHost.name || 'Event Organizer',
            email: mainHost.email || '',
            avatar: mainHost.avatar_url || undefined
          };
        }

        transformedEventData.coHosts = coHosts.map((host: any) => ({
          id: host.id,
          name: host.name || host.email.split('@')[0],
          email: host.email,
          avatar: host.avatar_url || undefined,
          status: host.status || 'pending', // Asignar estado del co-host
          invited_at: host.invited_at || new Date().toISOString() // Asignar
        }));
      }

      setEventData(transformedEventData);
    } catch (error) {
      console.error('Error fetching event data:', error);
      setError('An unexpected error occurred');
    } finally {
      setIsLoadingEvent(false);
    }
  };

  const handleEditEvent = () => {
    redirectTo(`/events/${eventId}/edit`);
  };

  const handleDeleteEvent = async () => {
    if (!user?.id || !eventId) return;

    try {
      const { error } = await Event.delete(eventId, user.id);
      
      if (error) {
        console.error('Error deleting event:', error);
        alert('Error deleting event: ' + error.message);
        return;
      }

      // Redirigir al dashboard después de eliminar
      redirectTo('/dashboard');
    } catch (error) {
      console.error('Error deleting event:', error);
      alert('An unexpected error occurred while deleting the event');
    }
  };
  
  const handleEventUpdate = () => {
    // Refresh event data after side events are updated
    if (eventId) {
      fetchEventData(eventId);
    }
  };

  const handleCloseSuccessMessage = () => {
    setShowSuccessMessage(false);
    // Limpiar el parámetro de la URL sin recargar la página
    const newUrl = window.location.pathname;
    window.history.replaceState({}, '', newUrl);
  };

  const siteUrl = typeof window !== 'undefined' ? window.location.origin : 'https://cap.com';
  const pageUrl = typeof window !== 'undefined' ? window.location.href : 'https://cap.com';

  const title = eventData ? `${eventData.title} | Panel del evento | Punta Tech Meetup` : 'Evento | Panel | Punta Tech Meetup';
  const description = eventData?.description || 'Gestioná asistentes, co-anfitriones y comunicaciones de tu evento en Punta Tech Meetup.';
  const image = eventData?.imageUrl || `${siteUrl}/icon.ico`;

  // Redirect to dashboard if no view permission
  if (userPermissions && !userPermissions.canView) {
    redirectTo('/dashboard');
    return null;
  }

  // Show loading while checking permissions
  if (isLoadingEvent || !userPermissions) {
    return (
      <AuthGuard requireAuth={true} isLoading={isLoading} isAuthenticated={isAuthenticated} isInitialized={isInitialized}>
        <Header onSignInClick={() => {}} />
        <div className="p-16 flex justify-center items-center">
          <div className="text-lg">Cargando evento...</div>
        </div>
      </AuthGuard>
    );
  }

  if (error || !eventData) {
    return (
      <AuthGuard requireAuth={true} isLoading={isLoading} isAuthenticated={isAuthenticated} isInitialized={isInitialized}>
        <Header onSignInClick={() => {}} />
        <div className="p-16 text-center">
          <div className="text-lg text-red-600">{error || 'Evento no encontrado'}</div>
          <button 
            onClick={() => redirectTo('/dashboard')} 
            className="mt-4 text-purple-600 hover:underline"
          >
            ← Volver
          </button>
        </div>
      </AuthGuard>
    );
  }

  // Only show side events tab for main events
  const isMainEvent = !eventData.parent_event_id;

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={pageUrl} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:image" content={image} />
      </Helmet>
      <AuthGuard requireAuth={true} isLoading={isLoading} isAuthenticated={isAuthenticated} isInitialized={isInitialized}>
      <Header onSignInClick={() => {}} />
      <div className="px-4 sm:px-6 lg:px-16 py-4 sm:py-6 lg:py-16 space-y-4 mt-16 sm:mt-16 lg:mt-12">
        {/* Success Message */}
        {showSuccessMessage && (
          <div className='mb-6 sm:mb-8 lg:mb-12'>
            <div className="max-w-full sm:max-w-2xl mx-auto">
              <div className="bg-purple-50 border-2 border-purple-300 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6 flex items-center justify-between">
                <div className="flex items-center gap-2 sm:gap-3 flex-1">
                  <div className="flex-1">
                    <h3 className="text-purple-600 font-semibold text-xs sm:text-sm">
                      {searchParams.get('created') === 'true' 
                        ? 'El evento se creó correctamente'
                        : 'El evento se actualizó correctamente'
                      }
                    </h3>
                  </div>
                </div>
                <button
                  onClick={handleCloseSuccessMessage}
                  className="text-purple-400 hover:text-purple-600 transition-colors ml-2 flex-shrink-0"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}

        <button 
          onClick={() => redirectTo('/dashboard')} 
          style={{color: '#6D29F6'}} 
          className="text-sm text-muted-foreground text-primary"
        >
          ←{'    '} Volver 
        </button>
        
        <div className="max-w-full">
          <EventCard
            eventData={eventData}
            
            onEdit={userPermissions.canEdit ? handleEditEvent : undefined}
            onDelete={userPermissions.role === 'creator' ? handleDeleteEvent : undefined}
            canEdit={userPermissions.canEdit}
            canDelete={userPermissions.role === 'creator'}
          />
        </div>
        
        <div className="mt-4 sm:mt-6">
          <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as any)} defaultValue="guests">
            <div className="border-b mb-4">
              <TabsList className="bg-transparent p-0 w-full justify-start">
                {/* Tabs wrapper for horizontal scroll on mobile */}
                <div className="flex overflow-x-auto pb-1 min-w-full">
                  {/* Always show guests tab */}
                  <TabsTrigger 
                    value="guests"
                    className="bg-transparent border-0 rounded-none px-3 sm:px-4 py-2 border-b-2 border-transparent data-[state=active]:border-[#00B383] data-[state=active]:bg-transparent data-[state=active]:shadow-none hover:text-[#00B383] text-sm whitespace-nowrap flex-shrink-0"
                  >
                    Invitados
                  </TabsTrigger>
                  
                  {/* Only show co-hosts tab if user can manage attendees */}
                  {userPermissions.canManageAttendees && (
                    <TabsTrigger 
                      value="cohosts"
                      className="bg-transparent border-0 rounded-none px-3 sm:px-4 py-2 border-b-2 border-transparent data-[state=active]:border-[#00B383] data-[state=active]:bg-transparent data-[state=active]:shadow-none hover:text-[#00B383] text-sm whitespace-nowrap flex-shrink-0"
                    >
                      Co-anfitriones
                    </TabsTrigger>
                  )}
                  
                  {/* Only show attendance tab if user can manage attendees */}
                  {userPermissions.canManageAttendees && (
                    <TabsTrigger 
                      value="attendance"
                      className="bg-transparent border-0 rounded-none px-3 sm:px-4 py-2 border-b-2 border-transparent data-[state=active]:border-[#00B383] data-[state=active]:bg-transparent data-[state=active]:shadow-none hover:text-[#00B383] text-sm whitespace-nowrap flex-shrink-0"
                    >
                      Colaboradores
                    </TabsTrigger>
                  )}
                  
                  {/* Only show communication tab if user can manageAttendees */}
                  {userPermissions.canManageAttendees && (
                    <TabsTrigger 
                      value="communication"
                      className="bg-transparent border-0 rounded-none px-3 sm:px-4 py-2 border-b-2 border-transparent data-[state=active]:border-[#00B383] data-[state=active]:bg-transparent data-[state=active]:shadow-none hover:text-[#00B383] text-sm whitespace-nowrap flex-shrink-0"
                    >
                      Comunicación
                    </TabsTrigger>
                  )}
                  
                  {/* Only show Side Events tab for main events and if user can edit */}
                  {!eventData.parent_event_id && userPermissions.canEdit && (
                    <TabsTrigger 
                      value="sideEvents"
                      className="bg-transparent border-0 rounded-none px-3 sm:px-4 py-2 border-b-2 border-transparent data-[state=active]:border-[#00B383] data-[state=active]:bg-transparent data-[state=active]:shadow-none hover:text-[#00B383] text-sm whitespace-nowrap flex-shrink-0"
                    >
                      Eventos secundarios
                    </TabsTrigger>
                  )}
                </div>
              </TabsList>
            </div>

            <div className="min-h-0 overflow-hidden">
              <TabsContent value="guests" className="mt-0">
                <GuestList eventId={eventData.id} guestLimit={eventData.limited_places} />
              </TabsContent>
              
              <TabsContent value="cohosts" className="mt-0">
                {userPermissions.canManageAttendees && (
                  <CoHostsManager 
                    eventId={eventData.id}
                    coHosts={eventData.coHosts}
                    onUpdate={handleEventUpdate}
                  />
                )}
              </TabsContent>
              
              <TabsContent value="attendance" className="mt-0">
                {userPermissions.canManageAttendees && (
                  <AttendanceManager 
                    eventId={eventData.id}
                    requiresApproval={eventData.requires_approval} 
                  />
                )}
              </TabsContent>
              
              <TabsContent value="communication" className="mt-0">
                {userPermissions.canManageAttendees && <CommunicationPanel eventId={eventData.id} eventTitle={eventData.title}   />}
              </TabsContent>
              
              <TabsContent value="sideEvents" className="mt-0">
                {!eventData.parent_event_id && userPermissions.canEdit && (
                  <div className="bg-white rounded-lg p-3 sm:p-4">
                    <SideEventsManager 
                      mainEventId={eventData.id}
                      userId={user?.id || ''}
                      onUpdate={handleEventUpdate}
                    />
                  </div>
                )}
              </TabsContent>
            </div>
          </Tabs>
        </div>
      </div>
      </AuthGuard>
    </>
  );
}

EventPage.displayName = "Punta Tech Meetup - Evento";
export default EventPage;